import { useEffect, useState } from 'react';

import { captureException, setTag } from '@sentry/nextjs';
import axios, { AxiosRequestConfig, AxiosError, AxiosResponse, AxiosInstance } from 'axios';
import EventSource from 'eventsource';

import APIResponse, { ResponseStatus } from './response';

interface OnPermissionDenied {
  (e: AxiosError<{ displayMessage: string }>): void;
}

export interface RequesterOptions {
  onPermissionDenied?: OnPermissionDenied;
}

export class Requester {
  private readonly onPermissionDenied?: OnPermissionDenied;

  get Instance() {
    return this.axios;
  }

  private validateSuccess(response: AxiosResponse, isRaw?: boolean) {
    if (isRaw) {
      return response.status === 200 || response.status === 204;
    }
    return response.data?.code === 'SUCCESS';
  }

  private async request<T>(
    params: AxiosRequestConfig,
    isRaw?: boolean,
  ): Promise<APIResponse<T, string>> {
    try {
      const response = await this.axios.request(params);

      if (this.validateSuccess(response, isRaw)) {
        return new APIResponse<T, string>(response.data, response.status, ResponseStatus.SUCCESS);
      }

      return new APIResponse<T, string>(
        response.data?.message || 'error',
        500,
        ResponseStatus.FAILURE,
        response.data.code,
        response.data.displayMessage,
      );
    } catch (error) {
      return this.handleError(error);
    }
  }

  public async get<T>(
    path: string,
    params?: AxiosRequestConfig,
    isRaw?: boolean,
  ): Promise<APIResponse<T, string>> {
    return this.request(
      {
        ...params,
        method: 'get',
        url: path,
      },
      isRaw,
    );
  }

  public async post<T>(
    path: string,
    params?: AxiosRequestConfig,
    isRaw?: boolean,
  ): Promise<APIResponse<T, string>> {
    return this.request(
      {
        ...params,
        method: 'post',
        url: path,
      },
      isRaw,
    );
  }

  public async put<T>(
    path: string,
    params?: AxiosRequestConfig,
    isRaw?: boolean,
  ): Promise<APIResponse<T, string>> {
    return this.request(
      {
        ...params,
        method: 'put',
        url: path,
      },
      isRaw,
    );
  }

  public async patch<T>(
    path: string,
    params?: AxiosRequestConfig,
    isRaw?: boolean,
  ): Promise<APIResponse<T, string>> {
    return this.request(
      {
        ...params,
        method: 'patch',
        url: path,
      },
      isRaw,
    );
  }

  public async delete<T>(
    path: string,
    params?: AxiosRequestConfig,
    isRaw?: boolean,
  ): Promise<APIResponse<T, string>> {
    return this.request(
      {
        ...params,
        method: 'delete',
        url: path,
      },
      isRaw,
    );
  }

  private captureApiError(
    statusCode: number | undefined,
    errorUrl: string,
    message: string | undefined,
    status: number | undefined,
  ): void {
    setTag('type', 'API');

    const baseErrorMessage = `[API ERROR ${statusCode}]\n\nAPI_URL: ${errorUrl}`;
    const errorMessage =
      typeof window !== 'undefined'
        ? `${baseErrorMessage}\n\nURL:${window.location.pathname}`
        : baseErrorMessage;

    const errorContext = {
      contexts: {
        error: {
          message,
          status,
        },
      },
    };

    captureException(new AxiosError(errorMessage), errorContext);
  }

  private handleError<T>(e: unknown): APIResponse<T, string> {
    if (!('isAxiosError' in (e as AxiosError))) {
      captureException(e);
      return new APIResponse<T, string>(null, 500, ResponseStatus.FAILURE);
    }

    const error = e as AxiosError;

    // axios error 응답 제대로 못받은 case
    if (!error.response) {
      captureException(error);
      return new APIResponse<T, string>(null, 500, ResponseStatus.FAILURE);
    }

    const status = (() => {
      const parsed = error?.response?.status;
      return !parsed || parsed === 200 ? error?.response?.status : parsed;
    })();

    const message = error?.message;
    const errorUrl = error?.config?.baseURL + '' + (error?.config?.url + '').replace('/', '');

    if (error?.response?.status === 403) {
      this.onPermissionDenied?.(error as AxiosError<{ displayMessage: string }>);
    }

    // noise 방지를 위해 HTTP status 401과 403은 sentry 에러를 받지 않는다.
    if (error?.response?.status !== 401 && error?.response?.status !== 403) {
      this.captureApiError(error?.response?.status, errorUrl, message, status);
    }

    const responseData = error.response.data as any;
    return new APIResponse<T, string>(
      error.response.data as string,
      error.response.status,
      ResponseStatus.FAILURE,
      responseData?.code,
      responseData?.displayMessage || responseData?.message,
    );
  }

  constructor(
    host: string,
    basePath = '',
    config: AxiosRequestConfig = {},
    options?: RequesterOptions,
  ) {
    this.axios = axios.create({
      baseURL: `${host}/${basePath}`,
      timeout: 300000,
      ...config,
    });
    this.axios.defaults.timeout = 300000;
    this.onPermissionDenied = options?.onPermissionDenied;
  }
  axios: AxiosInstance;
}

export const useEventSource = <T>(
  /** 요청 url */
  url: string,
  /** message 왔을시에 실행할 callback */
  callback: (e: MessageEvent<T>) => void,
  /** run조건 */
  enabled: boolean,
  /** 추가로 필요한 option들 */
  options?: EventSource.EventSourceInitDict,
  onUnauthenticated?: () => void,
) => {
  const [reconnect, setReconnect] = useState(false);
  const [currentConnection, setCurrentConnection] = useState<EventSource | null>(null);

  useEffect(() => {
    if (enabled || reconnect) {
      if (reconnect) {
        setReconnect(false);
      }

      // 이미 연결이 있다면 새로운 연결을 생성하지 않음
      if (currentConnection) {
        return;
      }

      const sse = new EventSource(url, options);
      setCurrentConnection(sse);

      sse.addEventListener('message', (e) => {
        callback(e);
      });

      sse.onerror = (e) => {
        if (e.status === 401 || e.status === 403) {
          sse.close();
          setCurrentConnection(null);
          onUnauthenticated?.();
          return;
        }
        sse.close();
        setCurrentConnection(null);
        setTimeout(() => {
          if (!reconnect) {
            console.info('reconnect event source');
            setReconnect(true);
          }
        }, 10000);
      };

      return () => {
        sse.close();
        setCurrentConnection(null);
      };
    }
  }, [enabled, reconnect]);

  return {
    reconnect,
    setReconnect,
  };
};
